'use client';

/**
 * Type declaration for the Meta (Facebook) Pixel global function.
 */
type FbqFunction = {
  (action: 'init', pixelId: string): void;
  (action: 'track', event: string, params?: Record<string, unknown>): void;
  (
    action: 'track',
    event: string,
    params: Record<string, unknown>,
    options: { eventID: string },
  ): void;
  (
    action: 'trackCustom',
    event: string,
    params?: Record<string, unknown>,
  ): void;
};

declare global {
  interface Window {
    fbq?: FbqFunction;
  }
}

/**
 * Returns the fbq function if the Meta Pixel has been loaded, or null.
 */
function getFbq(): FbqFunction | null {
  if (typeof window === 'undefined') {
    return null;
  }

  if (typeof window.fbq !== 'function') {
    return null;
  }

  return window.fbq;
}

/**
 * Initializes the Meta Pixel using the NEXT_PUBLIC_META_PIXEL_ID environment
 * variable. If no pixel ID is set, initialization is skipped silently.
 * This is the recommended entry point for automatic setup.
 */
export function initMetaPixelFromEnv(): void {
  const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID;

  if (!pixelId) {
    return;
  }

  initMetaPixel(pixelId);
}

/**
 * Initializes the Meta Pixel by injecting the tracking script and calling
 * fbq('init', pixelId). Also reads the fbclid query parameter and stores
 * it in a first-party cookie for offline conversion attribution.
 */
export function initMetaPixel(pixelId: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // Avoid double-init
  if (typeof window.fbq === 'function') {
    return;
  }

  // Meta Pixel base code: create a lightweight queue-based stub that
  // buffers calls until the real fbevents.js script loads.
  interface FbqStub {
    (...args: unknown[]): void;
    callMethod?: (...args: unknown[]) => void;
    push: FbqStub;
    loaded: boolean;
    version: string;
    queue: unknown[][];
  }

  const stub = function fbqStubFn(...args: unknown[]) {
    if (stub.callMethod) {
      stub.callMethod(...args);
    } else {
      stub.queue.push(args);
    }
  } as unknown as FbqStub;

  stub.push = stub;
  stub.loaded = true;
  stub.version = '2.0';
  stub.queue = [];

  window.fbq = stub as unknown as FbqFunction;

  const script = document.createElement('script');

  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');

  // Store fbclid for offline conversion attribution
  storeFbclid();
}

/**
 * Fires the standard Meta "Lead" conversion event.
 */
export function trackMetaLead(
  eventId?: string,
  value?: number,
  currency?: string,
): void {
  const fbq = getFbq();

  if (!fbq) return;

  const params: Record<string, unknown> = {};

  if (value !== undefined) {
    params.value = value;
    params.currency = currency ?? 'USD';
  }

  if (eventId) {
    fbq('track', 'Lead', params, { eventID: eventId });
  } else {
    fbq('track', 'Lead', params);
  }
}

/**
 * Reads the fbclid query parameter from the current URL and persists it
 * in a first-party cookie (_fbc) so it is available for server-side
 * conversion API calls.
 */
function storeFbclid(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  try {
    const url = new URL(window.location.href);
    const fbclid = url.searchParams.get('fbclid');

    if (!fbclid) return;

    const timestamp = Math.floor(Date.now() / 1000);
    const fbcValue = `fb.1.${timestamp}.${fbclid}`;

    // Set cookie with 90-day expiry, SameSite=Lax for security
    const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);

    document.cookie = `_fbc=${fbcValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  } catch {
    // Silently ignore URL parsing errors
  }
}
